import type { TFunction, i18n } from 'i18next';

import type {
  GuestAgeRange,
  GuestContact,
  GuestRelationship,
  VolunteerContact,
} from '@sola/shared-utils/supporters/volunteers.ts';
import { type VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { selectFallbackImage } from '@sola/skippy-search-sdk/lib/volunteer-image.ts';
import { deriveInitialsFromName } from '@sola/supporter-sdk/utils/name-format.ts';
import {
  doDateRangesOverlap,
  formatDate,
  formatDateRange,
  formatEventDate,
  formatSlotDate,
  formatTime,
} from '@sola/utils/date.ts';

// ── Types ────────────────────────────────────────────────────────────────────

export interface VolunteerRoleRules {
  minAge?: 14 | 16 | 18;
}

/** An exact alias for a canonical role: matches a specific API description
 * string and carries its own display label, inheriting rules and
 * contentPosition from the parent role. */
export interface VolunteerRoleAlias {
  /** Exact `description` string from the API this alias matches. */
  match: string;
  /** Display label for this alias (parent's rules/contentPosition are inherited). */
  labelKey: string;
}

export interface VolunteerRoleConfig {
  labelKey: string;
  rules: VolunteerRoleRules;
  contentPosition?: string;
  /** Exact aliases owned by this role. Each inherits `rules` and
   * `contentPosition` from this entry but keeps its own `labelKey`. */
  aliases?: VolunteerRoleAlias[];
}

export interface VolunteerSlot {
  id: string;
  eventLocationId: string;
  startDate: string;
  endDate: string;
  status: string;
  description: string | null;
  remainingCapacity: number;
}

/** Maps contactId → array of slotIds they are assigned to. */
export type ShiftAssignments = Record<string, string[]>;

// ── Contact types ────────────────────────────────────────────────────────────

// Live in shared-utils; re-exported so existing consumers keep their imports.
export type {
  GuestAgeRange,
  GuestContact,
  GuestRelationship,
  KnownContact,
  VolunteerContact,
} from '@sola/shared-utils/supporters/volunteers.ts';

export const isMinor = (ageRange: GuestAgeRange): boolean => ageRange !== '18+';

const ageRangeMinAge: Record<GuestAgeRange, number> = {
  '14-15': 14,
  '16-17': 16,
  '18+': 18,
};

export const meetsAgeRequirement = (
  ageRange: GuestAgeRange,
  minAge: number,
): boolean => ageRangeMinAge[ageRange] >= minAge;

const guestRelationshipApiLabel: Record<GuestRelationship, string> = {
  self: 'Self',
  child: 'Resident Child',
  'nonresident-child': 'Nonresident Child',
  spouse: 'Spouse',
  friend: 'Friend',
  relative: 'Relative',
  other: 'Other',
};

export const getRelationshipApiLabel = (
  relationship: GuestRelationship,
): string => guestRelationshipApiLabel[relationship];

// ── Shared type guards & helpers ─────────────────────────────────────────────

/** Type guard: narrows VolunteerContact to GuestContact. */
export const isGuestContact = (c: VolunteerContact): c is GuestContact =>
  'relationship' in c;

/**
 * Maps raw supporter contacts to VolunteerContacts. Defaults to visible: only
 * an explicit `canVolunteer === false` excludes a contact, so missing/null
 * values stay eligible during the deploy window before the backend populates
 * `canVolunteer`.
 *
 * @TODO: Once v3 contacts guarantee `canVolunteer`, tighten to
 * `.filter((c) => c.canVolunteer)` with a non-null type.
 */
export const mapContacts = (
  raw: Array<{
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    preferredName?: string | null;
    email?: string | null;
    phone?: string | null;
    canVolunteer?: boolean | null;
  }> | null,
): VolunteerContact[] =>
  (raw ?? [])
    .filter((c) => c.canVolunteer !== false)
    .map((c) => ({
      id: c.id,
      firstName: c.firstName ?? null,
      lastName: c.lastName ?? null,
      preferredName: c.preferredName ?? null,
      email: c.email ?? null,
      phone: c.phone ?? null,
    }));

// ── New-guest conflict validation ────────────────────────────────────────────

/** Reason a guest can't be added: exact name+email match, or a non-minor reusing an email. */
export type GuestConflict = 'duplicate' | 'emailInUse';

const normalize = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase();

/** Checks a new guest against the selected pool; returns the conflict or null if valid. */
export const validateNewGuest = (
  newGuest: Pick<GuestContact, 'firstName' | 'lastName' | 'email' | 'ageRange'>,
  existing: VolunteerContact[],
): GuestConflict | null => {
  const firstName = normalize(newGuest.firstName);
  const lastName = normalize(newGuest.lastName);
  const email = normalize(newGuest.email);

  // No exact name + email duplicate, regardless of age.
  const isDuplicate = existing.some(
    (contact) =>
      normalize(contact.firstName) === firstName &&
      normalize(contact.lastName) === lastName &&
      normalize(contact.email) === email,
  );
  if (isDuplicate) {
    return 'duplicate';
  }

  // A non-minor's email must be unique — but it may match a minor guest already
  // added (the parent reusing the email their minors share). Only an email
  // already held by another non-minor is a conflict.
  if (!isMinor(newGuest.ageRange)) {
    const emailTaken = existing.some((contact) => {
      if (normalize(contact.email) !== email) {
        return false;
      }
      const isMinorGuest = isGuestContact(contact) && isMinor(contact.ageRange);
      return !isMinorGuest;
    });
    if (emailTaken) {
      return 'emailInUse';
    }
  }

  return null;
};

/**
 * Formats a contact's display name in one of three variants. Each resolves the
 * given name the same way — first name, else preferred name:
 * - `'full'` (default): given name + last name.
 * - `'short'`: given name, falling back to last name so it's never blank.
 * - `'initials'`: initials derived from given name + last name.
 */
export const getContactDisplayName = (
  contact: {
    firstName?: string | null;
    lastName?: string | null;
    preferredName?: string | null;
  },
  { variant = 'full' }: { variant?: 'short' | 'full' | 'initials' } = {},
): string => {
  const last = contact.lastName?.trim() ?? '';
  const givenName =
    (contact.firstName?.trim() || contact.preferredName?.trim()) ?? '';

  if (variant === 'short') {
    return givenName || last;
  }

  const fullName = [givenName, last].filter(Boolean).join(' ');

  if (variant === 'initials') {
    return deriveInitialsFromName(fullName) || '';
  }

  return fullName;
};

// ── Slot grouping key ────────────────────────────────────────────────────────

/** Slots with the same key represent interchangeable positions. */
export const getSlotGroupKey = (slot: {
  description?: string | null;
  startDate: string;
  endDate: string;
}): string =>
  `${slot.description ?? 'General'}__${slot.startDate}__${slot.endDate}`;

/** Chronological comparator: orders slots by start time, then end time. */
export const compareSlotByTime = (
  a: { startDate: string; endDate: string },
  b: { startDate: string; endDate: string },
): number =>
  new Date(a.startDate).getTime() - new Date(b.startDate).getTime() ||
  new Date(a.endDate).getTime() - new Date(b.endDate).getTime();

// ── Registration helpers ─────────────────────────────────────────────────────

/** Tracks slot group keys (name__start__end) that were previously registered per contact. */
export type PreviousRegistrations = Record<string, string[]>;

/**
 * Builds a map of previous registrations from already-registered slots.
 * Returns the registrations map and a Set of previously registered contact IDs.
 */
export const buildPreviousRegistrations = (
  slots: Array<{
    contactId: string;
    shiftName?: string | null;
    startDate: string;
    endDate: string;
  }>,
): {
  previousRegistrations: PreviousRegistrations;
  previouslyRegisteredContactIds: Set<string>;
} => {
  const previousRegistrations: PreviousRegistrations = {};
  const previouslyRegisteredContactIds = new Set<string>();

  for (const slot of slots) {
    if (!previousRegistrations[slot.contactId]) {
      previousRegistrations[slot.contactId] = [];
    }
    const groupKey = getSlotGroupKey({
      description: slot.shiftName,
      startDate: slot.startDate,
      endDate: slot.endDate,
    });
    previousRegistrations[slot.contactId].push(groupKey);
    previouslyRegisteredContactIds.add(slot.contactId);
  }

  return { previousRegistrations, previouslyRegisteredContactIds };
};

// ── Media helpers ────────────────────────────────────────────────────────────

/**
 * Hosts that are explicitly allowed in next.config remotePatterns for
 * volunteer images. URLs from these hosts are safe for `next/image` and
 * should not be replaced with a fallback.
 */
const ALLOWED_IMAGE_HOST_PATTERNS = [
  /\.ctfassets\.net$/,
  /\.content\.tenovos\.io$/,
  /\.ci\.org$/,
  /\.compassion\.com$/,
  /^compassionmedia\.com$/,
  /^compassion\.my\.salesforce\.com$/,
  /^compassion--usstage\.sandbox\.my\.salesforce\.com$/,
  /^compassion--devintus\.sandbox\.my\.salesforce\.com$/,
] as const;

/**
 * Resolves the final volunteer image URL: returns the given URL if it is safe
 * for `next/image`, otherwise resolves to a deterministic local fallback.
 *
 * "Safe" means either:
 * - Root-relative (served from the app's public folder), or
 * - From a host explicitly allowed in next.config remotePatterns.
 *
 * This guard is a website-rendering concern (it encodes what `next/image`
 * will accept), so it lives here rather than in the search SDK. The indexer
 * only needs the shared fallback selection from the SDK.
 *
 * @param url - The raw image URL from the indexed document.
 * @param fallbackKey - A stable string (e.g. slug) used to pick a deterministic fallback.
 */
export const getVolunteerImageUrl = (
  url: string,
  fallbackKey: string,
): string => {
  const normalisedUrl = url.startsWith('//') ? `https:${url}` : url;
  if (normalisedUrl.startsWith('/')) {
    return normalisedUrl;
  }

  // Check if the URL is from an allowed external host over HTTPS. next.config
  // remotePatterns only permits `https`, so anything else (e.g. http://) must
  // fall back rather than being handed to next/image.
  try {
    const { protocol, hostname } = new URL(normalisedUrl);
    if (
      protocol === 'https:' &&
      ALLOWED_IMAGE_HOST_PATTERNS.some((pattern) => pattern.test(hostname))
    ) {
      return normalisedUrl;
    }
  } catch {
    // Malformed URL — fall through to fallback.
  }

  // Unrecognised external URL (e.g. stale CloudFront/S3 entries) — use fallback.
  return selectFallbackImage(fallbackKey).url;
};

/** Builds the media prop for a volunteer location image, or undefined when no image is available. */
export const buildLocationMedia = (
  location: Pick<VolunteerLocationItemDto, 'image' | 'locationName' | 'slug'>,
) =>
  location.image?.url &&
  (URL.canParse(location.image.url) || location.image.url.startsWith('/'))
    ? {
        image: {
          url: getVolunteerImageUrl(
            location.image.url,
            location.slug || location.locationName,
          ),
          alt: location.locationName,
          width: 700,
          height: 467,
        },
      }
    : undefined;

// ── Display formatting ───────────────────────────────────────────────────────

const getLastEndDate = (
  volunteer: Pick<VolunteerLocationItemDto, 'endDate' | 'tourEvents'>,
  isTour: boolean,
): string => {
  if (!isTour) {
    return volunteer.endDate;
  }
  const tourEvents = volunteer.tourEvents ?? [];
  return tourEvents.reduce(
    (max, e) => (e?.endDate && e.endDate > max ? e.endDate : max),
    volunteer.endDate,
  );
};

/**
 * Format a volunteer event's date range for display.
 * Treats dates as wall-clock times (volunteer dates have no timezone offset).
 */
export const formatEventDateRange = (
  volunteer: Pick<
    VolunteerLocationItemDto,
    'startDate' | 'endDate' | 'tourEvents' | 'tourStartDate' | 'tourEndDate'
  >,
  isTour: boolean,
  variant: 'default' | 'card' | 'event' = 'default',
): string => {
  const startDate = new Date(volunteer.startDate);

  // Event detail page:
  // Single day: "Thu • March 12, 2026 • 7:00 PM"
  // Multi-day:  "Thu, March 12 – Sat, March 14, 2026"
  if (variant === 'event' && !isTour) {
    return formatEventDate(volunteer.startDate, volunteer.endDate);
  }

  // Event card: "Sat, Mar 15 • 7:00 PM"
  if (variant === 'card' && !isTour) {
    const shortDate = formatDate(startDate, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${shortDate} \u2022 ${formatTime(startDate)}`;
  }

  // Default: date range
  // Prefer the tour's own start/end when set; otherwise derive from the
  // location's own dates (end falls back to the latest tourEvents date).
  const firstStartDate =
    (isTour && volunteer.tourStartDate) || volunteer.startDate;
  const lastEndDate =
    (isTour && volunteer.tourEndDate) || getLastEndDate(volunteer, isTour);

  return formatDateRange(firstStartDate, lastEndDate, {
    monthOnly: isTour,
  });
};

/**
 * Format a volunteer date range for share copy: "Month D, YYYY", localized,
 * collapsing to a single date when start and end fall on the same day and
 * showing the year once for a same-year range (it repeats only across years).
 * Uses the event's own start/end so share copy matches the date range shown on
 * the location page.
 *
 * Volunteer dates are naive wall-clock strings, so they are formatted as-is
 * (like formatEventDateRange) and the calendar day never shifts with the
 * viewer's timezone.
 */
export const formatShareDateRange = (
  volunteer: Pick<VolunteerLocationItemDto, 'startDate' | 'endDate'>,
  locale = 'en-US',
): string => {
  return formatDateRange(volunteer.startDate, volunteer.endDate, { locale });
};

/**
 * Build the localized share summary sentence for a volunteer opportunity.
 * Centralizes the `shareSummary` i18n key, date-range formatting, and
 * `escapeValue: false` so every share entry point stays consistent.
 */
export const buildShareSummary = (
  t: TFunction & { i18n?: i18n },
  {
    location,
    title,
  }: {
    location: Pick<
      VolunteerLocationItemDto,
      'startDate' | 'endDate' | 'locationName'
    >;
    /** Overrides `location.locationName` (e.g. a tour name). */
    title?: string;
  },
): string => {
  // The active language comes from the caller's own `t` — its bound i18n
  // instance is the source of truth, so callers never thread a locale through.
  const locale = t.i18n?.resolvedLanguage ?? t.i18n?.language ?? 'en-US';
  return t('volunteers:shareSummary', {
    locationName: title ?? location.locationName,
    dateRange: formatShareDateRange(location, locale),
    interpolation: { escapeValue: false },
  });
};

/**
 * Format a YYYY-MM-DD slot date-key for display (UTC, since date-keys are calendar dates).
 * Wraps formatSlotDate with utc:true baked in so callers don't repeat the option.
 */
export const formatSlotDateKey = (
  dateKey: string,
  style: 'long' | 'short' = 'long',
  includeYear = false,
  locale = 'en-US',
): string => formatSlotDate(dateKey, style, includeYear, locale, { utc: true });

const isNonEmptyString = (value: string | undefined): value is string =>
  typeof value === 'string' && value.length > 0;

const joinPresent = (
  values: Array<string | null | undefined>,
  separator: string,
): string =>
  values
    .map((value) => value?.trim())
    .filter(isNonEmptyString)
    .join(separator);

/** Build a Google Maps search URL from a free-text location query. */
export const buildMapsSearchUrl = (query: string): string =>
  `https://www.google.com/maps/search/${encodeURIComponent(query)}`;

/**
 * Build a `mailto:` link to a volunteer coordinator, prefilling the subject
 * with the location name.
 */
export const buildCoordinatorMailtoUrl = (
  email: string,
  subject: string,
): string =>
  `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}`;

/** Build display lines and an optional Google Maps query from location data. */
export const formatVolunteerAddress = (
  volunteer: Pick<
    VolunteerLocationItemDto,
    | 'venueName'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'postalCode'
    | 'country'
  >,
): { lines: string[]; mapQuery: string | null; mapsUrl: string | null } => {
  const locality = joinPresent([volunteer.city, volunteer.state], ', ');
  const localityAndPostalCode = joinPresent(
    [locality, volunteer.postalCode],
    ' ',
  );
  const lines = [
    ...new Set(
      [
        volunteer.venueName,
        volunteer.address1,
        volunteer.address2,
        localityAndPostalCode,
      ]
        .map((value) => value?.trim())
        .filter(isNonEmptyString),
    ),
  ];

  const mapQuery = volunteer.address1?.trim() ? lines.join(', ') : null;

  return {
    lines,
    mapQuery,
    mapsUrl: mapQuery ? buildMapsSearchUrl(mapQuery) : null,
  };
};

/**
 * Build a location display string, optionally showing tour location count.
 */
export const formatLocationDisplay = (
  volunteer: Pick<VolunteerLocationItemDto, 'city' | 'state' | 'tourEvents'>,
  isTour: boolean,
  isTourList: boolean,
  fallbackLabel = '',
): string => {
  const location = joinPresent([volunteer.city, volunteer.state], ', ');
  const tourEvents = volunteer.tourEvents ?? [];
  const uniqueTourLocations = isTour
    ? [
        ...new Set(
          tourEvents
            .map((event) => joinPresent([event.city, event.state], ', '))
            .filter(Boolean),
        ),
      ]
    : [];
  const otherLocationCount = uniqueTourLocations.length - (location ? 1 : 0);

  if (isTour && !isTourList && otherLocationCount > 0) {
    const suffix = `${otherLocationCount} other location${otherLocationCount > 1 ? 's' : ''}`;
    return location ? `${location} + ${suffix}` : suffix;
  }

  return location || fallbackLabel;
};

// ── Role matching & filtering ────────────────────────────────────────────────

/**
 * Keys match slot `description` from the API.
 *
 * Entries are kept in alphabetical order by key to avoid accidental duplicates.
 *
 * Resolution for an incoming description (see `getRoleConfig`):
 *   1. Exact map key match.
 *   2. Exact alias — a parent role's `aliases[].match`; inherits rules +
 *      contentPosition from the parent, keeps its own label.
 *   3. Fuzzy keyword fallback (see `roleKeywordFallbacks`) for unlisted variants.
 *   4. `Event Volunteer` default.
 */
export const volunteerRoleConfigs: Record<string, VolunteerRoleConfig> = {
  'Compassion Experience Teardown Volunteer': {
    labelKey: 'roles.tearDown',
    rules: { minAge: 18 },
    contentPosition: 'volunteerRoleSetUpTearDown',
    aliases: [
      {
        match: 'CompEx Teardown Volunteer',
        labelKey: 'roles.setUpTearDown',
      },
      {
        match: 'CompEx Tear-Down Volunteer',
        labelKey: 'roles.setUpTearDown',
      },
    ],
  },
  'Compassion Experience Setup Volunteer': {
    labelKey: 'roles.setUp',
    rules: { minAge: 18 },
    contentPosition: 'volunteerRoleSetUpTearDown',
    aliases: [
      {
        match: 'CompEx Setup Volunteer',
        labelKey: 'roles.setUpTearDown',
      },
      {
        match: 'CompEx Set-Up Volunteer',
        labelKey: 'roles.setUpTearDown',
      },
    ],
  },
  'Compassion Experience Volunteer': {
    labelKey: 'roles.compassionExperience',
    rules: { minAge: 14 },
    contentPosition: 'volunteerRoleCompEx',
  },
  'Event Facilitator': {
    labelKey: 'roles.eventFacilitator',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleEventFacilitator',
  },
  'Event Volunteer': {
    labelKey: 'roles.eventVolunteer',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleEventVolunteer',
  },
  General: {
    labelKey: 'roles.general',
    rules: {},
    contentPosition: 'volunteerRoleGeneral',
  },
  Greeter: {
    labelKey: 'roles.greeter',
    rules: {},
    contentPosition: 'volunteerRoleGreeter',
  },
  'Handout Volunteer': {
    labelKey: 'roles.handoutVolunteer',
    rules: { minAge: 14 },
    contentPosition: 'volunteerRoleHandout',
  },
  'Setup/Teardown Volunteer': {
    labelKey: 'roles.setUpTearDown',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleSetUpTearDown',
    aliases: [
      { match: 'Tear-Down Volunteer', labelKey: 'roles.setUpTearDown' },
      { match: 'Set-Up Volunteer', labelKey: 'roles.setUpTearDown' },
      { match: 'Teardown Volunteer', labelKey: 'roles.setUpTearDown' },
      { match: 'Setup Volunteer', labelKey: 'roles.setUpTearDown' },
      { match: 'Tear Down Volunteer', labelKey: 'roles.setUpTearDown' },
      { match: 'Set Up Volunteer', labelKey: 'roles.setUpTearDown' },
    ],
  },
  'Table Lead Volunteer': {
    labelKey: 'roles.tableLeadVolunteer',
    rules: { minAge: 18 },
    contentPosition: 'volunteerRoleTable',
  },
  'Table Volunteer': {
    labelKey: 'roles.tableVolunteer',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleTable',
  },
  'Virtual Reality Volunteer': {
    labelKey: 'roles.vrExperience',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleVR',
    aliases: [
      { match: 'VR Experience Volunteer', labelKey: 'roles.vrExperience' },
    ],
  },
  'Volunteer Entrance Host': {
    labelKey: 'roles.entranceHost',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleEntranceHost',
  },
};

const defaultVolunteerRole = 'Event Volunteer';

// Unknown/unmatched descriptions fall back to the Event Volunteer config
const defaultRoleConfig: VolunteerRoleConfig =
  volunteerRoleConfigs[defaultVolunteerRole];

/**
 * Normalize a role name for fuzzy matching.
 * Lowercases, strips hyphens, normalizes compound words, and collapses whitespace.
 */
export const normalizeRoleName = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replaceAll('-', ' ')
    .replaceAll('/', ' ')
    .replaceAll('setup', 'set up')
    .replaceAll('teardown', 'tear down')
    .replaceAll(/\s+/g, ' ');

/**
 * Fuzzy alias rules: for descriptions with no exact match, the first rule whose
 * keyword is contained in the normalized description wins. Order matters —
 * more specific rules should come first.
 *
 * The description is normalized with `normalizeRoleName`, so `setup`/`set-up`
 * collapse to `set up` and `teardown`/`tear-down` collapse to `tear down`.
 */
const roleKeywordFallbacks: {
  keywords: string[];
  role: keyof typeof volunteerRoleConfigs;
}[] = [
  { keywords: ['set up', 'tear down'], role: 'Setup/Teardown Volunteer' },
  { keywords: ['entrance host'], role: 'Volunteer Entrance Host' },
  { keywords: ['experience'], role: 'Compassion Experience Volunteer' },
  { keywords: ['facilitator'], role: 'Event Facilitator' },
];

/**
 * Index of exact aliases → resolved config, built from each role's `aliases`
 * array. An alias inherits its parent's `rules` and `contentPosition` but keeps
 * its own `labelKey`.
 */
const aliasIndex: Record<string, VolunteerRoleConfig> = Object.values(
  volunteerRoleConfigs,
).reduce<Record<string, VolunteerRoleConfig>>((acc, parent) => {
  for (const alias of parent.aliases ?? []) {
    acc[alias.match] = {
      labelKey: alias.labelKey,
      rules: parent.rules,
      contentPosition: parent.contentPosition,
    };
  }
  return acc;
}, {});

/** Return the canonical role name for a description via fuzzy keyword rules. */
const matchKeywordFallback = (
  description: string | null,
): string | undefined => {
  if (!description) {
    return undefined;
  }
  const normalized = normalizeRoleName(description);
  return roleKeywordFallbacks.find(({ keywords }) =>
    keywords.some((keyword) => normalized.includes(keyword)),
  )?.role;
};

/**
 * Resolve a role description to its config. See `volunteerRoleConfigs` for the
 * resolution order (exact key → exact alias → fuzzy keyword → default).
 */
export const getRoleConfig = (
  description: string | null,
): VolunteerRoleConfig => {
  const key = description ?? defaultVolunteerRole;

  const direct = volunteerRoleConfigs[key];
  if (direct) {
    return direct;
  }

  const alias = aliasIndex[key];
  if (alias) {
    return alias;
  }

  const fuzzyRole = matchKeywordFallback(description);
  if (fuzzyRole) {
    return volunteerRoleConfigs[fuzzyRole] ?? defaultRoleConfig;
  }

  return defaultRoleConfig;
};

/**
 * Find ALL role slot counts that match a content item title.
 * Used for combined roles (e.g. "Setup/Teardown Volunteer" should aggregate
 * slots from both "Set-Up Volunteer" and "Tear-Down Volunteer").
 *
 * Matching tiers (a role is included if ANY tier matches):
 * 1. Exact normalized match
 * 2. Bidirectional substring containment
 * 3. Component matching — for compound titles (containing multiple words before
 *    "volunteer"), each component word group is checked against roles individually
 *    (e.g. "set up tear down volunteer" matches "set up volunteer" and "tear down volunteer")
 * 4. Event-prefix fallback — if title starts with "event", include any role
 *    that also starts with "event"
 */
export const findAllMatchingRoles = (
  title: string,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number }[] => {
  const titleNorm = normalizeRoleName(title);

  const matches = roleCounts.filter((rc) => {
    const roleNorm = normalizeRoleName(rc.role);

    // Tier 1: exact match
    if (roleNorm === titleNorm) {
      return true;
    }

    // Tier 2: bidirectional substring
    if (roleNorm.includes(titleNorm) || titleNorm.includes(roleNorm)) {
      return true;
    }

    // Tier 3: component matching for compound titles
    // Split the title into word groups separated by known role suffixes (e.g. "volunteer")
    // "set up tear down volunteer" → components ["set up", "tear down"]
    const suffix = 'volunteer';
    const titleWithoutSuffix = titleNorm.endsWith(suffix)
      ? titleNorm.slice(0, -suffix.length).trim()
      : titleNorm;
    const roleWithoutSuffix = roleNorm.endsWith(suffix)
      ? roleNorm.slice(0, -suffix.length).trim()
      : roleNorm;

    // Check if the role's core (without suffix) is a component of the title
    if (
      titleWithoutSuffix.length > roleWithoutSuffix.length &&
      roleWithoutSuffix.length > 0
    ) {
      // Split title core into words and check if the role core words appear
      // as a contiguous sequence
      const titleWords = titleWithoutSuffix.split(' ');
      const roleWords = roleWithoutSuffix.split(' ');
      for (let i = 0; i <= titleWords.length - roleWords.length; i++) {
        const slice = titleWords.slice(i, i + roleWords.length);
        if (slice.join(' ') === roleWords.join(' ')) {
          return true;
        }
      }
    }

    // Tier 3b: setup/teardown keyword matching
    // A compound title containing "set up" and/or "tear down" should match
    // any role that also contains one of those keywords (regardless of prefixes
    // like "compex" or "compassion experience")
    const setupTeardownKeywords = ['set up', 'tear down'];
    const titleKeywords = setupTeardownKeywords.filter((kw) =>
      titleNorm.includes(kw),
    );
    if (titleKeywords.length > 0) {
      const roleHasKeyword = setupTeardownKeywords.some((kw) =>
        roleNorm.includes(kw),
      );
      if (roleHasKeyword) {
        return true;
      }
    }

    return false;
  });

  // Tier 4: event-prefix fallback (only when no matches found above)
  if (matches.length === 0) {
    const titlePrefix = titleNorm.split(' ')[0];
    if (titlePrefix === 'event') {
      return roleCounts.filter((rc) =>
        normalizeRoleName(rc.role).startsWith('event'),
      );
    }
  }

  return matches;
};

/**
 * Find all role slot counts that belong to a content item, matched by
 * `contentPosition` rather than by (alias- or locale-dependent) title.
 *
 * Each `roleCount.role` is resolved to its `contentPosition` via `getRoleConfig`
 * and kept when it intersects the item's positions. This keeps aliased roles —
 * whose title never matches a canonical role name but whose `contentPosition`
 * is inherited from the parent role — from being dropped after slots refresh.
 */
export const findRolesByContentPosition = (
  itemContentPositions: string[] | undefined,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number }[] => {
  const positions = new Set(itemContentPositions);
  if (positions.size === 0) {
    return [];
  }
  return roleCounts.filter((rc) => {
    const position = getRoleConfig(rc.role).contentPosition;
    return !!position && positions.has(position);
  });
};

/**
 * Map a list of API role names to the set of `contentPosition` identifiers
 * they resolve to (via their role config). Role names with no configured
 * position are ignored.
 */
export const getContentPositionsForRoles = (roles: string[]): Set<string> =>
  new Set(
    roles
      .map((role) => getRoleConfig(role).contentPosition)
      .filter((p): p is string => !!p),
  );

/**
 * Check if a content item is present in a tour, matched by `contentPosition`
 * rather than by (locale-dependent) title.
 */
export const hasMatchingContentPosition = (
  itemContentPositions: string[] | undefined,
  tourContentPositions: Set<string>,
): boolean =>
  (itemContentPositions ?? []).some((position) =>
    tourContentPositions.has(position),
  );

/**
 * Enrich role content items with live slot count badges, or filter by indexed roles.
 *
 * When `roleCounts` is available (live data loaded), keeps every role that is
 * offered at the location (has a matching count) and attaches an open-slots
 * badge only when there are slots to fill — a full role still shows, badge-less.
 *
 * When `roleCounts` is not yet available, falls back to filtering items by the
 * indexed `roles` array (no badge).
 *
 * Display order follows the authored order of the parent `content.roles.items`;
 * this function only filters (and badges) — it never reorders.
 */
export const buildFilteredRoleItems = <
  T extends { title?: string | null; contentPosition?: string[] },
>(
  items: T[] | undefined,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
  indexedRoles: string[],
  formatBadgeLabel: (openSlots: number) => string,
): (T & { badge?: { label: string; color: string; size: string } })[] => {
  if (!items) {
    return [];
  }

  if (roleCounts) {
    return items.flatMap((item) => {
      // Prefer contentPosition matching (stable across aliases/locales); fall
      // back to title matching only when the item carries no contentPosition.
      const matches =
        (item.contentPosition?.length ?? 0) > 0
          ? findRolesByContentPosition(item.contentPosition, roleCounts)
          : findAllMatchingRoles(item.title ?? '', roleCounts);

      // A role that isn't offered at this location has no matching count — drop
      // it. Offered roles always show; the badge appears only when slots are open.
      if (matches.length === 0) {
        return [];
      }

      const combinedOpenSlots = matches.reduce(
        (sum, m) => sum + m.openSlots,
        0,
      );
      return [
        {
          ...item,
          badge:
            combinedOpenSlots > 0
              ? {
                  label: formatBadgeLabel(combinedOpenSlots),
                  color: 'brand',
                  size: 'sm',
                }
              : undefined,
        },
      ];
    });
  }

  // Fallback (no live data): filter items by the location's indexed roles,
  // matched by contentPosition (stable across locales) rather than by title.
  const indexedContentPositions = getContentPositionsForRoles(indexedRoles);
  return items
    .filter((item) =>
      hasMatchingContentPosition(item.contentPosition, indexedContentPositions),
    )
    .map((item) => ({ ...item }));
};

// ── Slot grouping & deduplication ────────────────────────────────────────────

export interface RawSlot {
  id: string;
  eventLocationId: string;
  startDate: string;
  endDate: string;
  status: string;
  description?: string | null;
}

/**
 * Extract the date portion from an ISO-ish datetime string (e.g. "2026-09-26").
 * Works with both naive local times and UTC strings.
 */
export const getSlotDateKey = (isoString: string): string =>
  isoString.slice(0, 10);

/**
 * Collapses available slots with the same role + time into grouped entries.
 * `remainingCapacity` reflects how many individual slots remain.
 */
export const deduplicateSlots = (rawSlots: RawSlot[]): VolunteerSlot[] => {
  const available = rawSlots.filter((s) => s.status === 'Available');

  const groupMap = new Map<string, { ids: string[]; slot: RawSlot }>();
  for (const slot of available) {
    const key = getSlotGroupKey(slot);
    const existing = groupMap.get(key);
    if (existing) {
      existing.ids.push(slot.id);
    } else {
      groupMap.set(key, { ids: [slot.id], slot });
    }
  }

  return [...groupMap.values()].map(({ ids, slot }) => ({
    id: ids[0],
    eventLocationId: slot.eventLocationId,
    startDate: slot.startDate,
    endDate: slot.endDate,
    status: slot.status,
    description: slot.description ?? null,
    remainingCapacity: ids.length,
  }));
};

/** Maps each slot group key to all available raw slot IDs in that group. */
export const buildSlotGroupIds = (
  rawSlots: RawSlot[],
): Map<string, string[]> => {
  const available = rawSlots.filter((s) => s.status === 'Available');
  const pool = new Map<string, string[]>();
  for (const slot of available) {
    const key = getSlotGroupKey(slot);
    const ids = pool.get(key) ?? [];
    ids.push(slot.id);
    pool.set(key, ids);
  }
  return pool;
};

/**
 * Resolve a contact's assigned slot ids to their shifts (one per group), from
 * the raw feed - `remainingCapacity` isn't meaningful here.
 */
export const getContactShifts = (
  assignedSlotIds: string[],
  rawSlots: RawSlot[],
): VolunteerSlot[] => {
  const byGroup = new Map<string, VolunteerSlot>();
  for (const id of assignedSlotIds) {
    const slot = rawSlots.find((s) => s.id === id);
    if (!slot) {
      continue;
    }
    const key = getSlotGroupKey(slot);
    if (!byGroup.has(key)) {
      byGroup.set(key, {
        ...slot,
        description: slot.description ?? null,
        remainingCapacity: 0,
      });
    }
  }
  return [...byGroup.values()].toSorted(compareSlotByTime);
};

/** Groups slots by role label, sorted by start time within each group. */
export const groupSlotsByRole = (
  slots: VolunteerSlot[],
  fallbackLabel: string,
): Array<[string, VolunteerSlot[]]> => {
  const byRole: Record<string, VolunteerSlot[]> = {};
  for (const slot of slots) {
    const key = slot.description ?? fallbackLabel;
    const group = byRole[key] ?? [];
    group.push(slot);
    byRole[key] = group;
  }
  for (const [key, group] of Object.entries(byRole)) {
    byRole[key] = group.toSorted(compareSlotByTime);
  }
  return Object.entries(byRole);
};

/**
 * Group a sorted array of slots by their date. Returns entries in chronological order.
 * Only meaningful for multi-day events — single-day events return a single group.
 */
export const groupSlotsByDate = (
  slots: VolunteerSlot[],
): Array<[string, VolunteerSlot[]]> => {
  const byDate: Record<string, VolunteerSlot[]> = {};
  for (const slot of slots) {
    const dateKey = getSlotDateKey(slot.startDate);
    const group = byDate[dateKey] ?? [];
    group.push(slot);
    byDate[dateKey] = group;
  }
  return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
};

// ── Conflict detection & capacity ────────────────────────────────────────────

/** Checks whether a contact has a time conflict with the given slot. */
export const hasSlotTimeConflict = (
  contactId: string,
  targetSlot: VolunteerSlot,
  allSlots: VolunteerSlot[],
  assignments: ShiftAssignments,
  slotGroupIds: Map<string, string[]>,
): boolean => {
  const contactAssignedIds = assignments[contactId] ?? [];
  const assignedSlots = allSlots.filter((s) => {
    const groupIds = slotGroupIds.get(getSlotGroupKey(s)) ?? [];
    return (
      s.id !== targetSlot.id &&
      contactAssignedIds.some((id) => groupIds.includes(id))
    );
  });
  return assignedSlots.some((s) =>
    doDateRangesOverlap(
      s.startDate,
      s.endDate,
      targetSlot.startDate,
      targetSlot.endDate,
    ),
  );
};

/** Checks whether a slot's capacity is exhausted for a specific contact. */
export const isSlotAtCapacity = (
  contactId: string,
  slot: VolunteerSlot,
  pool: string[],
  assignments: ShiftAssignments,
): boolean => {
  const othersAssigned = new Set(
    Object.entries(assignments)
      .filter(([cId]) => cId !== contactId)
      .flatMap(([, ids]) => ids),
  );
  const takenByOthers = pool.filter((id) => othersAssigned.has(id)).length;
  return takenByOthers >= slot.remainingCapacity;
};
